import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
    Box, Typography, Paper, Tabs, Tab,
    List, ListItem, ListItemButton, ListItemText, TextField, Collapse,
    Grid, IconButton, Button
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Add as AddIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import Editor from '@monaco-editor/react';
import { Controller } from 'react-hook-form';
import { getMonacoLanguage } from '../utils/getMonacoLanguage';
import { TrainingBasicInfoForm, Step2LineAnswersInput } from './index';
import { MAX_STEP_FILES } from '@/constants/trainings';
import {
    RESIZABLE_THREE_COLUMNS,
    ResizeColumnHandle,
    useResizableThreeColumns,
} from '../utils/resizableThreeColumns';
/**
 * 훈련 수정 모드 컴포넌트
 * 
 * @param {Object} props
 * @param {Object} props.control - react-hook-form의 control 객체
 * @param {Object} props.errors - react-hook-form의 errors 객체
 * @param {Function} props.watch - react-hook-form의 watch 함수
 * @param {Function} props.getValues - react-hook-form의 getValues 함수
 * @param {Function} props.setValue - react-hook-form의 setValue 함수
 * @param {Array} props.step1Fields - 1단계 파일 필드 배열
 * @param {Array} props.step2Fields - 2단계 파일 필드 배열
 * @param {Function} props.appendStep1 - 1단계 파일 추가 함수
 * @param {Function} props.appendStep2 - 2단계 파일 추가 함수
 * @param {Function} props.removeStep1 - 1단계 파일 삭제 함수
 * @param {Function} props.removeStep2 - 2단계 파일 삭제 함수
 * @param {Array} props.categories - 카테고리 목록
 * @param {Array} props.subCategories - 서브카테고리 목록
 * @param {number|null} props.selectedCategoryId - 선택된 카테고리 ID
 * @param {Function} props.onCategoryChange - 카테고리 변경 핸들러
 * @param {Function} props.onSubCategoryChange - 서브카테고리 변경 핸들러
 * @param {Function} props.onLanguageChange - 언어 변경 핸들러
 */
const TrainingDetailEdit = ({
    control,
    errors,
    watch,
    getValues,
    setValue,
    step1Fields,
    step2Fields,
    appendStep1,
    appendStep2,
    removeStep1,
    removeStep2,
    categories,
    subCategories,
    selectedCategoryId,
    onCategoryChange,
    onSubCategoryChange,
    onLanguageChange,
}) => {
    // 수정 모드용 상위 탭 선택 상태
    const [activeMainTab, setActiveMainTab] = useState(0);

    // 수정 모드용 선택된 파일 인덱스
    const [selectedFileIndex, setSelectedFileIndex] = useState(0);

    // 수정 모드용 정답 입력 라인 (표시)
    const [step2SelectedLine, setStep2SelectedLine] = useState(null);

    // 수정 모드용 힌트 표시 여부
    const [showHint, setShowHint] = useState(true);

    // 수정 모드용 Monaco Editor refs
    const editorRefs = useRef({});
    const decorationsRefs = useRef({});

    // 수정 모드용 현재 파일 목록
    const currentFiles = useMemo(() => {
        return activeMainTab === 0 ? step1Fields : step2Fields;
    }, [activeMainTab, step1Fields, step2Fields]);

    const canAddMoreFiles = useMemo(
        () => currentFiles.length < MAX_STEP_FILES,
        [currentFiles.length]
    );

    // 수정 모드용 현재 선택된 파일
    const currentFile = useMemo(() => {
        if (currentFiles.length === 0) return null;
        return currentFiles[selectedFileIndex] || currentFiles[0];
    }, [currentFiles, selectedFileIndex]);

    // 수정 모드용 라인 파싱 헬퍼 (1단계: "1,2,3" 문자열 / 2단계: 포커스된 단일 라인 번호 number)
    const getLineNumbers = useCallback((val) => {
        if (val === null || val === undefined || val === '') return [];
        if (typeof val === 'number' && !Number.isNaN(val)) return [val];
        if (Array.isArray(val)) return val;
        if (typeof val === 'string' && val.trim()) {
            return val.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n));
        }
        return [];
    }, []);

    // 수정 모드용 Decoration 업데이트 로직 (1단계: 모든 라인에 glyph 표시하여 클릭 가능하게)
    const updateDecorations = useCallback((editor, lines, editorKey) => {
        if (!editor || !editor.getModel()) return;

        const decorationIds = decorationsRefs.current[editorKey] || [];
        const lineNumbers = getLineNumbers(lines);
        const selectedLinesSet = new Set(lineNumbers);
        const totalLines = editor.getModel().getLineCount();
        const newDecos = [];

        for (let lineNumber = 1; lineNumber <= totalLines; lineNumber++) {
            const isSelected = selectedLinesSet.has(lineNumber);
            newDecos.push({
                range: { startLineNumber: lineNumber, startColumn: 1, endLineNumber: lineNumber, endColumn: 1 },
                options: {
                    isWholeLine: true,
                    className: isSelected ? 'selected-line-highlight' : '',
                    glyphMarginClassName: isSelected ? 'selected-line-glyph' : 'unselected-line-glyph',
                }
            });
        }

        try {
            const newIds = editor.deltaDecorations(decorationIds, newDecos);
            decorationsRefs.current[editorKey] = newIds;
        } catch (e) { console.warn(e); }
    }, [getLineNumbers]);

    // 수정 모드용 파일 추가 핸들러
    const handleAddFile = useCallback(() => {
        if (activeMainTab === 0) {
            if (step1Fields.length >= MAX_STEP_FILES) {
                errorToast(`파일은 단계당 최대 ${MAX_STEP_FILES}개까지 추가할 수 있습니다.`);
                return;
            }
            appendStep1({ fileName: '', content: '', lines: '' });
            setTimeout(() => {
                setSelectedFileIndex(step1Fields.length);
            }, 0);
        } else {
            if (step2Fields.length >= MAX_STEP_FILES) {
                errorToast(`파일은 단계당 최대 ${MAX_STEP_FILES}개까지 추가할 수 있습니다.`);
                return;
            }
            appendStep2({ fileName: '', content: '', word: '', lineAnswers: [] });
            setTimeout(() => {
                setSelectedFileIndex(step2Fields.length);
            }, 0);
        }
    }, [activeMainTab, appendStep1, appendStep2, step1Fields.length, step2Fields.length]);

    // 수정 모드용 파일 삭제 핸들러
    const handleRemoveFile = useCallback((index) => {
        if (activeMainTab === 0) {
            removeStep1(index);
            if (selectedFileIndex >= step1Fields.length - 1 && selectedFileIndex > 0) {
                setSelectedFileIndex(selectedFileIndex - 1);
            } else if (selectedFileIndex >= step1Fields.length - 1) {
                setSelectedFileIndex(0);
            }
        } else {
            removeStep2(index);
            if (selectedFileIndex >= step2Fields.length - 1 && selectedFileIndex > 0) {
                setSelectedFileIndex(selectedFileIndex - 1);
            } else if (selectedFileIndex >= step2Fields.length - 1) {
                setSelectedFileIndex(0);
            }
        }
    }, [activeMainTab, step1Fields.length, step2Fields.length, selectedFileIndex, removeStep1, removeStep2]);

    // 수정 모드용 단계 변경 핸들러
    const handleStepChange = useCallback((event, newValue) => {
        setActiveMainTab(newValue);
        setSelectedFileIndex(0);
        setStep2SelectedLine(null);
    }, []);

    // 수정 모드용 파일 선택 핸들러
    const handleFileSelect = useCallback((index) => {
        setSelectedFileIndex(index);
        setStep2SelectedLine(null);
    }, []);

    const {
        containerRef: mainAreaRef,
        leftWidth: explorerWidth,
        rightWidth: hintWidth,
        startResizeLeft: startResizeExplorer,
        startResizeRight: startResizeHint,
        handleWidthPx,
        minLeft: minExplorerWidth,
        minRight: minHintWidth,
        minCenter: minCenterWidth,
    } = useResizableThreeColumns();

    const handleClearSelectedLines = useCallback(() => {
        setValue(`step1Files.${selectedFileIndex}.lines`, '', {
            shouldDirty: true,
            shouldValidate: true
        });
        updateDecorations(editorRefs.current[`step1Files-${selectedFileIndex}`], '', `step1Files-${selectedFileIndex}`);
    }, [selectedFileIndex, setValue, updateDecorations]);

    // 2단계: 정답 입력 포커스로 step2SelectedLine만 바뀐 경우에도 에디터 데코레이션 갱신 + 해당 라인이 보이도록 스크롤
    useEffect(() => {
        if (activeMainTab !== 1) return;
        const editorKey = `step2Files-${selectedFileIndex}`;
        const editor = editorRefs.current[editorKey];
        if (!editor) return;
        updateDecorations(editor, step2SelectedLine, editorKey);

        if (typeof step2SelectedLine === 'number' && !Number.isNaN(step2SelectedLine)) {
            const model = editor.getModel();
            if (!model) return;
            const total = model.getLineCount();
            const line = Math.min(Math.max(1, step2SelectedLine), total);
            editor.revealLineInCenter(line);
        }
    }, [activeMainTab, selectedFileIndex, step2SelectedLine, updateDecorations]);

    return (
        <>
            {/* 기본 정보 폼 */}
            <Paper sx={{ p: 2, mb: 2, border: '1px solid #e0e0e0' }}>
                <Typography variant='h6' sx={{ fontWeight: 600, mb: 2 }}>기본 정보</Typography>
                <Grid container spacing={2}>
                    <TrainingBasicInfoForm
                        control={control}
                        errors={errors}
                        categories={categories}
                        subCategories={subCategories}
                        selectedCategoryId={selectedCategoryId}
                        onCategoryChange={onCategoryChange}
                        onSubCategoryChange={onSubCategoryChange}
                        onLanguageChange={onLanguageChange}
                    />
                </Grid>
            </Paper>

            {/* 단계 탭 */}
            <Box sx={{ mb: 2 }}>
                <Tabs value={activeMainTab} onChange={handleStepChange}>
                    <Tab label="1단계" />
                    <Tab label="2단계" />
                </Tabs>
            </Box>

            {/* 메인 영역: VS Code 스타일 레이아웃 (리사이즈 핸들로 열 너비 조절) */}
            <Box
                ref={mainAreaRef}
                sx={{
                    display: 'flex',
                    flex: 1,
                    alignItems: 'stretch',
                    minHeight: 0,
                    width: '100%',
                }}
            >
                {/* 왼쪽: 파일 탐색기 */}
                <Paper
                    sx={{
                        width: explorerWidth,
                        minWidth: minExplorerWidth,
                        maxWidth: `${RESIZABLE_THREE_COLUMNS.maxLeftPercent}%`,
                        flexShrink: 0,
                        overflow: 'auto',
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box sx={{ p: 1, bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant='body2' fontWeight={600}>
                            파일 탐색기
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={handleAddFile}
                            color="primary"
                            disabled={!canAddMoreFiles}
                            title={canAddMoreFiles ? '파일 추가' : `최대 ${MAX_STEP_FILES}개까지 추가 가능`}
                        >
                            <AddIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <List dense sx={{ p: 0, flex: 1, overflow: 'auto' }}>
                        {currentFiles.map((field, index) => {
                            const fileName = watch(activeMainTab === 0 ? `step1Files.${index}.fileName` : `step2Files.${index}.fileName`);
                            return (
                                <ListItem key={field.id} disablePadding>
                                    <ListItemButton
                                        selected={index === selectedFileIndex}
                                        onClick={() => handleFileSelect(index)}
                                        sx={{
                                            '&.Mui-selected': {
                                                bgcolor: '#e3f2fd',
                                            }
                                        }}
                                    >
                                        <ListItemText
                                            primary={fileName || `파일 ${index + 1}`}
                                            primaryTypographyProps={{
                                                fontSize: '0.875rem',
                                                sx: {
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word',
                                                    overflowWrap: 'break-word',
                                                },
                                            }}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveFile(index);
                                            }}
                                            color="error"
                                            sx={{ ml: 1 }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                        {currentFiles.length === 0 && (
                            <Box sx={{ p: 2, textAlign: 'center' }}>
                                <Typography variant='body2' color='text.secondary'>
                                    파일이 없습니다.
                                </Typography>
                                <Button
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={handleAddFile}
                                    disabled={!canAddMoreFiles}
                                    sx={{ mt: 1 }}
                                >
                                    파일 추가
                                </Button>
                            </Box>
                        )}
                    </List>
                </Paper>

                <ResizeColumnHandle
                    ariaLabel="파일 탐색기와 에디터 너비 조절"
                    onMouseDown={startResizeExplorer}
                    handleWidthPx={handleWidthPx}
                />

                {/* 중앙: 에디터 영역 */}
                <Box sx={{ flex: 1, minWidth: minCenterWidth, display: 'flex', flexDirection: 'column' }}>
                    {currentFile ? (
                        <>
                            {/* 파일 탭 바 */}
                            <Paper sx={{ p: 1, bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant='body2' fontWeight={500}>
                                    {watch(activeMainTab === 0 ? `step1Files.${selectedFileIndex}.fileName` : `step2Files.${selectedFileIndex}.fileName`) || `파일 ${selectedFileIndex + 1}`}
                                </Typography>
                                {activeMainTab === 0 &&
                                    <Button
                                        size="small"
                                        onClick={handleClearSelectedLines}
                                    >
                                        선택된 라인 모두 지우기
                                    </Button>
                                }
                            </Paper>

                            {/* 파일명 입력 - key로 탭/파일 전환 시 리마운트하여 올바른 값 표시 */}
                            <Paper sx={{ p: 1.5, borderBottom: '1px solid #e0e0e0' }}>
                                <Controller
                                    key={`fileName-${activeMainTab}-${selectedFileIndex}`}
                                    control={control}
                                    name={activeMainTab === 0 ? `step1Files.${selectedFileIndex}.fileName` : `step2Files.${selectedFileIndex}.fileName`}
                                    rules={{ required: '파일명을 입력해주세요.' }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label="파일명"
                                            size="small"
                                            fullWidth
                                            placeholder="예: Main.java"
                                            error={!!errors[activeMainTab === 0 ? 'step1Files' : 'step2Files']?.[selectedFileIndex]?.fileName}
                                        />
                                    )}
                                />
                            </Paper>

                            {/* Monaco Editor - key로 탭/파일 전환 시 리마운트하여 올바른 내용 표시 */}
                            <Box sx={{ flex: 1, minHeight: 500, border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden', mt: 1 }}>
                                <Controller
                                    key={`content-${activeMainTab}-${selectedFileIndex}`}
                                    control={control}
                                    name={activeMainTab === 0 ? `step1Files.${selectedFileIndex}.content` : `step2Files.${selectedFileIndex}.content`}
                                    rules={{ required: '파일 내용을 입력해주세요.' }}
                                    render={({ field }) => {
                                        const fileName = watch(activeMainTab === 0 ? `step1Files.${selectedFileIndex}.fileName` : `step2Files.${selectedFileIndex}.fileName`);
                                        const editorKey = activeMainTab === 0 ? `step1Files-${selectedFileIndex}` : `step2Files-${selectedFileIndex}`;
                                        const linesValue = activeMainTab === 0 ? watch(`step1Files.${selectedFileIndex}.lines`) : step2SelectedLine;
                                        return (
                                            <Editor
                                                key={editorKey}
                                                height="100%"
                                                language={getMonacoLanguage(fileName)}
                                                theme="vs-light"
                                                defaultValue={field.value || ''}
                                                onChange={(val) => {
                                                    field.onChange(val || '');
                                                    const editor = editorRefs.current[editorKey];
                                                    updateDecorations(editor, linesValue, editorKey);
                                                }}
                                                onMount={(editor, monaco) => {
                                                    editorRefs.current[editorKey] = editor;

                                                    if (activeMainTab === 0) {
                                                        updateDecorations(editor, linesValue, editorKey);

                                                        editor.onMouseDown((e) => {
                                                            const isLineClick = e.target.type === monaco.editor.MouseTargetType.GUTTER_LINE_NUMBERS ||
                                                                e.target.type === monaco.editor.MouseTargetType.GUTTER_GLYPH_MARGIN;
                                                            if (isLineClick && e.target.position?.lineNumber) {
                                                                const clickedLine = e.target.position.lineNumber;
                                                                const currentString = getValues(`step1Files.${selectedFileIndex}.lines`);
                                                                const currentLines = getLineNumbers(currentString);

                                                                let newLines;
                                                                if (currentLines.includes(clickedLine)) {
                                                                    newLines = currentLines.filter(n => n !== clickedLine);
                                                                } else {
                                                                    newLines = [...currentLines, clickedLine].sort((a, b) => a - b);
                                                                }

                                                                const newString = newLines.join(',');
                                                                setValue(`step1Files.${selectedFileIndex}.lines`, newString, {
                                                                    shouldDirty: true,
                                                                    shouldValidate: true
                                                                });
                                                                updateDecorations(editor, newString, editorKey);
                                                            }
                                                        });
                                                    } else {
                                                        updateDecorations(editor, linesValue, editorKey);
                                                    }
                                                }}
                                                options={{
                                                    minimap: { enabled: true },
                                                    fontSize: 14,
                                                    scrollBeyondLastLine: false,
                                                    wordWrap: 'on',
                                                    readOnly: false,
                                                    glyphMargin: activeMainTab === 0,
                                                    selectOnLineNumbers: false,
                                                    lineNumbersMinChars: 3,
                                                }}
                                            />
                                        );
                                    }}
                                />
                            </Box>

                            {/* 정답 입력 영역 */}
                            <Paper sx={{ p: 2, mt: 2, border: '1px solid #e0e0e0' }}>
                                {activeMainTab === 0 ? (
                                    <Controller
                                        key={`lines-${selectedFileIndex}`}
                                        control={control}
                                        name={`step1Files.${selectedFileIndex}.lines`}
                                        render={({ field }) => (
                                            <TextField
                                                label="선택된 정답 라인 (에디터 라인 번호 클릭)"
                                                size="small"
                                                fullWidth
                                                value={field.value || ''}
                                                InputProps={{
                                                    readOnly: true,
                                                }}
                                                helperText="위 에디터에서 라인 번호를 클릭하면 자동으로 입력됩니다."
                                                sx={{ bgcolor: '#f5f5f5' }}
                                            />
                                        )}
                                    />
                                ) : (
                                    <Step2LineAnswersInput
                                        key={`step2-answers-${selectedFileIndex}`}
                                        setValue={setValue}
                                        watch={watch}
                                        selectedFileIndex={selectedFileIndex}
                                        setStep2SelectedLine={setStep2SelectedLine}
                                    />
                                )}
                            </Paper>
                        </>
                    ) : (
                        <Paper sx={{ p: 4, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                                파일이 없습니다.
                            </Typography>
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={handleAddFile}
                                disabled={!canAddMoreFiles}
                            >
                                파일 추가
                            </Button>
                        </Paper>
                    )}
                </Box>

                <ResizeColumnHandle
                    ariaLabel="에디터와 힌트 패널 너비 조절"
                    onMouseDown={startResizeHint}
                    handleWidthPx={handleWidthPx}
                />

                {/* 오른쪽: 힌트 패널 */}
                <Paper
                    sx={{
                        width: hintWidth,
                        minWidth: minHintWidth,
                        maxWidth: `${RESIZABLE_THREE_COLUMNS.maxRightPercent}%`,
                        flexShrink: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                    }}
                >
                    {/* 힌트 섹션 */}
                    <Box>
                        <Box
                            sx={{
                                p: 1.5,
                                bgcolor: '#f5f5f5',
                                borderBottom: '1px solid #e0e0e0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                cursor: 'pointer'
                            }}
                            onClick={() => setShowHint(!showHint)}
                        >
                            <Typography variant='body2' fontWeight={600}>
                                힌트
                            </Typography>
                            {showHint ? <ExpandLessIcon fontSize='small' /> : <ExpandMoreIcon fontSize='small' />}
                        </Box>
                        <Collapse in={showHint}>
                            <Box sx={{ p: 2 }}>
                                <Box sx={{ display: activeMainTab === 0 ? 'block' : 'none' }}>
                                    <Controller
                                        control={control}
                                        name='hint1'
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                multiline
                                                rows={6}
                                                label='1단계 힌트'
                                                variant='outlined'
                                                placeholder='1단계 문제 해결을 위한 힌트를 입력해주세요.'
                                            />
                                        )}
                                    />
                                </Box>
                                <Box sx={{ display: activeMainTab === 1 ? 'block' : 'none' }}>
                                    <Controller
                                        control={control}
                                        name='hint2'
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                multiline
                                                rows={6}
                                                label='2단계 힌트'
                                                variant='outlined'
                                                placeholder='2단계 문제 해결을 위한 힌트를 입력해주세요.'
                                            />
                                        )}
                                    />
                                </Box>
                            </Box>
                        </Collapse>
                    </Box>
                </Paper>
            </Box>
        </>
    );
};

export default TrainingDetailEdit;
